import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * storefrontColorRenders -- the per-color decorated renders for the product
 * editor's Storefront listing (Jake 2026-10-06, card
 * https://trello.com/c/Qlwx8mlT).
 *
 * STATE ONLY. The policy (30s debounce after the last save, Stage-switch
 * renders, cache check, concurrency cap, default-include) lives in
 * ``useStorefrontColorRenders``, which runs for the editor's whole lifetime
 * (it is mounted by ProductEditor.vue, not by the Storefront listing panel,
 * because saves and Stage switches happen on every tab). The panel only reads
 * this store to build its Available-images pool.
 *
 * Shape, keyed by color NAME (the storefront_images key):
 *
 *     { "<color>": { status: 'pending' | 'ready' | 'partial' | 'failed', url } }
 *
 *   - pending: the color's render is queued or in flight.
 *              url is always null -- the panel shows a shimmer tile.
 *   - ready:   a real render url exists for the color's CURRENT args.
 *   - partial: the composite DROPPED a placement (#312); url null, the
 *              panel shows a notice instead of a tile, ``dropped`` lists the
 *              backend's placement_* codes. Retried, never offered as a finished
 *              render. ``ready`` carries ``placements`` (the group's count).
 *   - failed:  the render failed; url null, no tile (fail toward the record).
 *
 * A color with no entry has no render and none is coming (not selected, no
 * bound artwork, unrouted, ... -- or simply already selected when the
 * product was opened, which renders nothing on its own).
 *
 * ``missingColors`` lists the selected colors that COULD render but have no
 * render (and none on its way); ``renderAll()`` is the deliberate backfill
 * for them -- the "Render all colors" button (Jake 2026-10-06). The manager
 * registers the handler; with no manager mounted it is a no-op.
 *
 * ``noteAutoSelectedColors`` lets a SYSTEM selection (the default-color pick
 * on a first SKU pick) tell the manager "this was not the distributor": such
 * a color stays dormant like one the product was opened with.
 */
export const useStorefrontColorRendersStore = defineStore(
    'storefrontColorRenders',
    () => {
        const renders = ref({})
        const missingColors = ref([])
        let renderAllHandler = null
        let renderColorHandler = null
        // Colors that need a render and have a trigger on its way (a save
        // debounce armed, or an edit whose autosave has not landed). The
        // manager publishes it; the Stage reads it through standInState.
        const scheduledColors = ref([])
        let autoSelectedSkus = new Set()

        function renderFor(color) {
            return renders.value[color] ?? null
        }

        function setRender(color, state) {
            renders.value = { ...renders.value, [color]: state }
        }

        function dropRender(color) {
            if (!(color in renders.value)) return
            const next = { ...renders.value }
            delete next[color]
            renders.value = next
        }

        function setMissingColors(colors) {
            missingColors.value = colors
        }

        function setRenderAllHandler(fn) {
            renderAllHandler = typeof fn === 'function' ? fn : null
        }

        // Unregister only if the handler is still THIS caller's: an editor
        // remount can register a new instance before the old one disposes.
        // Returns whether it did, so the caller also knows whether the shared
        // render state is still its to reset.
        function clearRenderAllHandler(fn) {
            if (renderAllHandler !== fn) return false
            renderAllHandler = null
            return true
        }

        function setScheduledColors(colors) {
            scheduledColors.value = colors
        }

        function setRenderColorHandler(fn) {
            renderColorHandler = typeof fn === 'function' ? fn : null
        }

        function clearRenderColorHandler(fn) {
            if (renderColorHandler !== fn) return false
            renderColorHandler = null
            return true
        }

        // The explicit render of ONE color (a Stage switch's path): the
        // "Render preview" action on the Stage stand-in.
        function renderColor(sku) {
            if (renderColorHandler) renderColorHandler(sku)
        }

        // What the Stage may honestly tell the distributor about a color that
        // has no decorated preview yet (card #338):
        //   'ready'     a render landed (its url is renderFor(color).url)
        //   'pending'   queued or in flight
        //   'scheduled' not started, but an existing trigger will start it
        //   'idle'      could render, nothing will start it: offer the action
        //   'none'      cannot render (no placement, no args): say nothing
        function standInState(color) {
            const r = renders.value[color]
            if (r?.status === 'ready' && r.url) return 'ready'
            if (r?.status === 'pending') return 'pending'
            if (scheduledColors.value.includes(color)) return 'scheduled'
            if (missingColors.value.includes(color)) return 'idle'
            return 'none'
        }

        function noteAutoSelectedColors(skus) {
            for (const sku of skus ?? []) autoSelectedSkus.add(sku)
        }

        // Hand the noted skus to the manager exactly once.
        function consumeAutoSelectedColors() {
            const out = autoSelectedSkus
            autoSelectedSkus = new Set()
            return out
        }

        function renderAll() {
            if (renderAllHandler) renderAllHandler()
        }

        function reset() {
            renders.value = {}
            missingColors.value = []
            scheduledColors.value = []
        }

        return {
            renders,
            missingColors,
            renderFor,
            setRender,
            dropRender,
            setMissingColors,
            scheduledColors,
            setScheduledColors,
            setRenderColorHandler,
            clearRenderColorHandler,
            renderColor,
            standInState,
            setRenderAllHandler,
            clearRenderAllHandler,
            noteAutoSelectedColors,
            consumeAutoSelectedColors,
            renderAll,
            reset,
        }
    },
)

export default useStorefrontColorRendersStore
